import { inject, ChangeDetectionStrategy, Component, computed, input, OnInit, output, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { Pessoa } from '@/shared/models/pessoa-info.model';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { CardContent } from '@/shared/components/card-content/card-content';
import { ConsultorioPrivadoService } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.service';
import { ConsultorioPrivadoResponse } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.model';
import { finalize } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TagModule } from 'primeng/tag';
import { Button } from 'primeng/button';

interface ConsultorioListaItem {
    controle: string;
    tipo: { value: string; color: string };
    endereco: string;
    cnes: string;
    situacao: { value: string; color: string };
    source: ConsultorioPrivadoResponse;
}

@Component({
    selector: 'app-consultorio-lista',
    standalone: true,
    imports: [BotaoAcoes, CardContent, CustomTableComponent, CardContainer, TagModule, CanDirective, Button],
    templateUrl: './consultorio-lista.component.html',
    styleUrl: './consultorio-lista.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Listagem dos consultórios privados da pessoa. */
export class ConsultorioListaComponent implements OnInit {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();
    protected readonly roleConsultorioEditar = PERMISSOES.medicos.consultorio.editar;
    rowClick = output<ConsultorioPrivadoResponse>();
    novoClick = output<void>();

    loading = signal<boolean>(false);
    data = signal<ConsultorioPrivadoResponse[]>([]);
    dataTabela = signal<ConsultorioListaItem[]>([]);
    selecionado = signal<ConsultorioPrivadoResponse | null>(null);
    /** Desabilita a ação "Novo" (ex.: médico não REGULAR não pode incluir consultório). */
    bloquearNovo = input<boolean>(false);
    /** Ações do menu, recomputadas conforme seleção e bloqueio de inclusão. */
    readonly acoes = computed<MenuItem[]>(() => [
        {
            label: 'Novo',
            icon: 'pi pi-plus',
            styleClass: 'bg-green-500',
            disabled: this.bloquearNovo(),
            command: () => this.novoClick.emit()
        },
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            styleClass: 'bg-blue-500',
            disabled: !this.selecionado(),
            command: () => this.onEditarSelecionado()
        }
    ]);
    metadataTabela = {
        headers: {
            controle: {
                type: 'text',
                default: '-',
                hidden: false,
                value: 'Nº Controle'
            },
            tipo: {
                type: 'tag',
                default: '-',
                hidden: false,
                value: 'Tipo'
            },
            endereco: {
                type: 'text',
                default: '-',
                hidden: false,
                value: 'Endereço'
            },
            cnes: {
                type: 'text',
                default: '-',
                hidden: false,
                value: 'CNES'
            },
            situacao: {
                type: 'tag',
                default: '-',
                hidden: false,
                value: 'Situação'
            }
        },
        title: 'controle',
        tag: 'situacao',
        actions: {
            visualizar: {
                label: 'Visualizar',
                icon: 'pi pi-eye',
                color: 'success',
                command: (item: ConsultorioListaItem) => this.onVisualizarLinha(item.source)
            }
        }
    } satisfies TableMetadata;

    constructor(private consultorioService: ConsultorioPrivadoService) {}

    ngOnInit(): void {
        this.carregarDados();
    }

    carregarDados(): void {
        const p = this.pessoa();
        if (!p || p.id == null || !p.uf) return;

        this.loading.set(true);
        this.consultorioService
            .listar({ id: p.id, uf: p.uf })
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (lista) => {
                    const dados = Array.isArray(lista) ? lista : [];
                    this.data.set(dados);
                    this.dataTabela.set(dados.map((item) => this.mapearParaTabela(item)));
                    this.limparSelecao();
                },
                error: (err) => {
                    this.logger.error('Erro ao carregar consultórios', err);
                    this.data.set([]);
                    this.dataTabela.set([]);
                    this.limparSelecao();
                }
            });
    }

    handleSelecaoLinha(item: ConsultorioListaItem | null): void {
        if (!item) {
            this.limparSelecao();
            return;
        }

        const row = item.source;
        if (this.isSelecionado(row)) {
            this.limparSelecao();
            return;
        }

        this.selecionado.set(row);
    }

    isSelecionado(row: ConsultorioPrivadoResponse): boolean {
        const atual = this.selecionado();
        if (!atual) return false;
        return atual.idConsultorio === row.idConsultorio;
    }

    private onEditarSelecionado(): void {
        const row = this.selecionado();
        if (!row) return;
        this.rowClick.emit(row);
    }

    onVisualizarLinha(row: ConsultorioPrivadoResponse): void {
        this.rowClick.emit(row);
    }

    private getSituacaoLabel(situacao?: string | null): string {
        switch (situacao) {
            case 'R':
                return 'Regular';
            case 'P':
                return 'Pendente';
            case 'C':
                return 'Cancelado';
            default:
                return situacao || '';
        }
    }

    private getSituacaoTagColor(situacao?: string | null): string {
        switch (situacao) {
            case 'R':
                return 'var(--p-green-500)';
            case 'P':
                return 'var(--p-yellow-500)';
            case 'C':
                return 'var(--p-red-500)';
            default:
                return 'var(--p-surface-500)';
        }
    }

    private getTipoLabel(tipo?: string | null): string {
        return tipo === 'E' ? 'Exclusivo' : 'Compartilhado';
    }

    private getTipoTagColor(tipo?: string | null): string {
        return tipo === 'E' ? 'var(--p-teal-500)' : 'var(--p-purple-500)';
    }

    private mapearParaTabela(item: ConsultorioPrivadoResponse): ConsultorioListaItem {
        return {
            controle: item.controle || '-',
            tipo: {
                value: this.getTipoLabel(item.inUso),
                color: this.getTipoTagColor(item.inUso)
            },
            endereco: `${item.txEndereco || ''}${item.nmBairro ? `, ${item.nmBairro}` : ''}` || '-',
            cnes: item.nuCnes || '-',
            situacao: {
                value: this.getSituacaoLabel(item.inSituacao) || '-',
                color: this.getSituacaoTagColor(item.inSituacao)
            },
            source: item
        };
    }

    private limparSelecao(): void {
        this.selecionado.set(null);
    }

    obterControleExibicao(item: ConsultorioListaItem): string {
        if (!item || !item.controle) return 'N/A';
        return item.controle.length > 70 ? item.controle.substring(0, 70) + '...' : item.controle;
    }

    obterEnderecoExibicao(item: ConsultorioListaItem): string {
        if (!item || !item.endereco) return 'N/A';
        return item.endereco.length > 70 ? item.endereco.substring(0, 70) + '...' : item.endereco;
    }
}
